import { Box, Link, Stack, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { GlassPanel } from '../../../shared/components/GlassPanel';
import { Pill } from '../../../shared/components/Pill';
import type { EntityMarketFeed } from '../../../core/types/data-models';
import { MARKET_FEED_IMPORTANCE_COLOR } from '../marketFeedImportance';

/** One news/article/social result — entity, resource, date, importance, description, and a link to the source. */
function MarketFeedItemCard({ item }: { item: EntityMarketFeed }) {
  return (
    <GlassPanel sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary">
            {item.entityName}
          </Typography>
          <Typography sx={{ fontWeight: 700, lineHeight: 1.3 }}>{item.resourceName}</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
          <Pill color={MARKET_FEED_IMPORTANCE_COLOR[item.importance]}>{item.importance}</Pill>
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {new Date(item.publishedAt).toLocaleDateString()}
          </Typography>
        </Stack>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {item.description}
      </Typography>
      <Link
        href={item.url}
        target="_blank"
        rel="noopener noreferrer"
        variant="caption"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}
      >
        View source <OpenInNewIcon sx={{ fontSize: 13 }} />
      </Link>
    </GlassPanel>
  );
}

/** "Market Feed" tab content — every recent web result found for this entity type's entities, most recent first. */
export function MarketFeedTab({ items, typeLabel }: { items: EntityMarketFeed[]; typeLabel: string }) {
  if (items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
        No recent market feed yet — click "Find Market News" to research the past week's news for these {typeLabel.toLowerCase()} entities.
      </Typography>
    );
  }
  return (
    <Stack spacing={1.5}>
      {items.map((item) => (
        <MarketFeedItemCard key={item.id} item={item} />
      ))}
    </Stack>
  );
}
